import { NgClass } from '@angular/common';
import { Component, ElementRef, computed, input, linkedSignal, OnInit, resource, signal, ViewChild, viewChild } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { Culture, cultures, Environment, EnvironmentService, ImageResizeService, Portal, Tenant, ThemePalette, TranslationPipe, UtilityService } from '@cloud-and-service/portal-lib';
import { Guid } from 'guid-typescript';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { TabDirective, TabsetComponent } from 'ngx-bootstrap/tabs';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { UpdatePasswordRequest } from 'src/app/models/update-password-request';
import { ColorService } from 'src/app/services/color.service';
import { TenantService } from 'src/app/services/tenant.service';
import { MomentPipe } from '../../pipes/moment.pipe';
import { AdminConnectivityTestComponent } from '../admin-connectivity-test/admin-connectivity-test.component';

@Component({
  selector: 'app-admin-tenant-detail',
  templateUrl: './admin-tenant-detail.component.html',
  styleUrls: ['./admin-tenant-detail.component.scss'],
  imports: [
    FormsModule,
    NgClass,
    TabsetComponent,
    TabDirective,
    MomentPipe,
    TranslationPipe,
    AdminConnectivityTestComponent,
  ],
})
export class AdminTenantDetailComponent implements OnInit {
  private static readonly DEFAULT_COLOR = '#24447f';

  @ViewChild('tenantprefix') tenantprefix!: ElementRef;
  @ViewChild('portalroute') portalroute!: ElementRef;

  tenantId = input<string | null>(null);

  tenant = linkedSignal<Tenant>(() => {
    return this.tenantResource.value() || {} as Tenant;
  });
  portalThemes: ThemePalette[] = [];
  fileToUpload: File = {} as File;
  allowSave: boolean = false;
  environment: Environment;
  cultures: Culture[];
  newGraphColor: string = AdminTenantDetailComponent.DEFAULT_COLOR;

  graphColorList = computed<string[]>(() => this.tenant().portal?.graphColors ?? []);

  tenantResource = resource({
    params: () => ({ tenantId: this.tenantId() }),
    loader: (args): Promise<Tenant | undefined> => {
      const tenantId = args.params.tenantId;
      if (this.utilityService.isEmpty(this.tenant().id) && tenantId) {
        return this.tenantService.getTenant(tenantId);
      }
      return Promise.resolve(undefined);
    }
  });

  public onClose: Subject<any>;
  pageId: string = 'nav';

  allowTest = signal(true);
  form = viewChild<NgForm>('form');

  constructor(
    private tenantService: TenantService,
    private colorService: ColorService,
    private utilityService: UtilityService,
    private toastrService: ToastrService,
    private imageResizeService: ImageResizeService,
    public bsModalRef: BsModalRef,
    environmentService: EnvironmentService
  ) {
    this.onClose = new Subject();
    this.environment = environmentService.getEnvironment();
    this.cultures = cultures;
  }

  ngOnInit() {
    const tenant = this.tenant();
    if (this.utilityService.isEmpty(tenant.portal)) {
      this.tenant.update(t => ({ ...t, portal: {} as Portal }));
    }
    this.validateFields();
    this.tenantService.getThemes().then((result: ThemePalette[]) => {
      this.portalThemes = result;
      this.refreshNextGraphColor();
    });
    this.refreshNextGraphColor();
  }

  showPage(pagename: string): void {
    this.pageId = pagename;
  }

  validateFields() {
    const tenant = this.tenant();
    this.allowSave =
      !this.utilityService.isEmpty(tenant.prefix) &&
      !this.utilityService.isEmpty(tenant.name) &&
      !this.utilityService.isEmpty(tenant.portal.route) &&
      this.validateLicenceId(tenant.licenceId);
  }

  validateLicenceId(licenceId: string | null): boolean {
    return (!this.utilityService.isEmpty(licenceId) && Guid.isGuid(licenceId)) || !this.environment.licenceRequired;
  }

  async addTenant() {
    const tenant = this.tenant();
    if (await this.tenantService.prefixExists(tenant.prefix)) {
      this.toastrService.error(`Prefix: [${tenant.prefix}] bruges allerede.`);
      this.pageId = 'nav';
      this.tenantprefix.nativeElement.focus();
      return;
    }

    if (
      !this.utilityService.isEmpty(tenant.portal.route) &&
      (await this.tenantService.routeExists(tenant.portal.route))
    ) {
      this.toastrService.error(`Url-rute: [${tenant.portal.route}] bruges allerede.`);
      this.pageId = 'portal';
      this.portalroute.nativeElement.focus();
      return;
    }

    this.tenantService.addTenant(tenant).then(() => {
      this.onClose.next(true);
      this.bsModalRef?.hide();
      this.toastrService.success(`${tenant.name} er oprettet.`);
    });
  }

  updateTenant() {
    const tenant = this.tenant();
    if (!this.utilityService.isEmpty(this.fileToUpload.size) && this.fileToUpload.size != 0) {
      this.tenantService.uploadLogo(tenant.portal.route, this.fileToUpload);
      this.tenant.update(t => ({ ...t, portal: { ...t.portal, logoType: this.fileToUpload.type } }));
    }
    this.tenantService.update(this.tenant()).then(() => {
      this.onClose.next(true);
      this.bsModalRef?.hide();
      this.toastrService.success(`Ændringer til ${tenant.name} er gemt.`);
    });
  }

  updateTenantServicePassword() {
    const tenant = this.tenant();
    if (!tenant.id || tenant.id == Guid.EMPTY) {
      this.toastrService.error(`Tenant skal være gemt før der kan tilføjes et servicePassword.`);
      return;
    }

    if (!tenant.servicePassword || !tenant.servicePassword.length) {
      this.toastrService.error(`Der skal være et password`);
      return;
    }

    let request: UpdatePasswordRequest = {} as UpdatePasswordRequest;
    request.id = tenant.id;
    request.password = tenant.servicePassword;
    this.tenantService.updateTenantServicePassword(request).then(() => {
      this.toastrService.success(`Ændringer til ${this.tenant.name} er gemt.`);
    });
  }

  updateTenantRestBasicAuthPassword() {
    const tenant = this.tenant();
    if (!tenant.id || tenant.id == Guid.EMPTY) {
      this.toastrService.error(`Tenant skal være gemt før der kan tilføjes et servicePassword.`);
      return;
    }

    if (!tenant.restBasicAuthPassword || !tenant.restBasicAuthPassword.length) {
      this.toastrService.error(`Der skal være et password`);
      return;
    }

    let request: UpdatePasswordRequest = {} as UpdatePasswordRequest;
    request.id = tenant.id;
    request.password = tenant.restBasicAuthPassword;
    this.tenantService.updateTenantRestBasicAuthPassword(request).then(() => {
      this.toastrService.success(`Ændringer til ${tenant.name} er gemt.`);
    });
  }

  updateTenantRestOAuthSecret() {
    const tenant = this.tenant();
    if (!tenant.id || tenant.id == Guid.EMPTY) {
      this.toastrService.error(`Tenant skal være gemt før der kan tilføjes et servicePassword.`);
      return;
    }

    if (!tenant.restOAuthClientSecret || !tenant.restOAuthClientSecret.length) {
      this.toastrService.error(`Der skal være et password`);
      return;
    }

    let request: UpdatePasswordRequest = {} as UpdatePasswordRequest;
    request.id = tenant.id;
    request.password = tenant.restOAuthClientSecret;
    this.tenantService.updateTenantRestOAuthSecret(request).then(() => {
      this.toastrService.success(`Ændringer til ${tenant.name} er gemt.`);
    });
  }

  validroute() {
    const tenant = this.tenant();
    if (tenant.portal.route?.length) {
      const route = tenant.portal.route
        .replace('_', '-')
        .replace(',', '-')
        .replace('.', '-')
        .replace(/[^a-zA-Z0-9-]/g, '')
        .toLowerCase();
      this.tenant.update(t => ({ ...t, portal: { ...t.portal, route: route } }));
    }
    this.validateFields();
  }

  compareThemes(p1: string, p2: string): boolean {
    if (p1 && p2) {
      return p1 === p2;
    }
    return false;
  }

  get selectedPalette(): ThemePalette | undefined {
    return this.portalThemes.find(t => t.name === this.tenant().portal?.uiTheme);
  }

  selectPalette(theme: ThemePalette): void {
    this.tenant.update(t => ({ ...t, portal: { ...t.portal, uiTheme: theme.name } }));
    if (!this.graphColorList().length) {
      this.refreshNextGraphColor();
    }
  }

  setPortalColor(field: string, value: string): void {
    this.tenant.update(t => ({ ...t, portal: { ...t.portal, [field]: value } }));
    if (field === 'primaryColor' && !this.graphColorList().length) {
      this.refreshNextGraphColor();
    }
  }

  clearPortalColor(field: string): void {
    this.tenant.update(t => ({ ...t, portal: { ...t.portal, [field]: null } }));
    if (field === 'primaryColor' && !this.graphColorList().length) {
      this.refreshNextGraphColor();
    }
  }

  addGraphColor(color: string): void {
    const current = this.tenant().portal?.graphColors ?? [];
    this.tenant.update(t => ({ ...t, portal: { ...t.portal, graphColors: [...current, color] } }));
    this.refreshNextGraphColor();
  }

  updateGraphColor(index: number, color: string): void {
    const current = [...(this.tenant().portal?.graphColors ?? [])];
    current[index] = color;
    this.tenant.update(t => ({ ...t, portal: { ...t.portal, graphColors: current } }));
    if (index === current.length - 1) {
      this.refreshNextGraphColor();
    }
  }

  removeGraphColor(index: number): void {
    const current = [...(this.tenant().portal?.graphColors ?? [])];
    current.splice(index, 1);
    this.tenant.update(t => ({ ...t, portal: { ...t.portal, graphColors: current.length > 0 ? current : null } }));
    this.refreshNextGraphColor();
  }

  async onFileUploadChange(event: any) {
    const tenant = this.tenant();
    if (event.target.files.length) {
      const file: File | null = event.target.files.item(0);
      if (file !== null) {
        this.fileToUpload = await this.imageResizeService.resize(file);
        tenant.portal.logo = this.fileToUpload.name;
      }
    }
  }

  disableTest() {
    this.allowTest.set(false);
  }

  private refreshNextGraphColor(): void {
    const colors = this.graphColorList();
    const base = colors.at(-1) ?? this.tenant().portal?.primaryColor ?? this.selectedPalette?.primary ?? AdminTenantDetailComponent.DEFAULT_COLOR;
    this.newGraphColor = this.colorService.deriveNextColor(base, colors.length + 1, AdminTenantDetailComponent.DEFAULT_COLOR);
  }
}
